import { type AlbumCollectionModel } from '@/shared/api'
import { ROUTES } from '@/shared/config'
import { showDeleteConfirmationModal } from '@/shared/lib/antd-utils'
import { navigateToFx } from '@/shared/lib/providers'
import { COMMON_COLUMN_WIDTH } from '@/shared/lib/styling'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { Button, Space, type TableProps, Typography } from 'antd'
import { useUnit } from 'effector-react'

import { type AlbumCollectionsSortState } from '../../lib/album-collections-query-model'
import { COLUMN_WIDTH } from '../../lib/column-width'
import { albumCollectionDeleted } from '../../model/album-collection.model'

interface useAlbumCollectionsTableColumnsProps {
  onEdit: (albumCollectionId: string) => void
  sort: AlbumCollectionsSortState
}
export const useAlbumCollectionsTableColumns = ({
  onEdit: handleEdit,
  sort,
}: useAlbumCollectionsTableColumnsProps): TableProps<AlbumCollectionModel>['columns'] => {
  const deleteAlbumCollection = useUnit(albumCollectionDeleted)
  const handleDelete = (id: string) => {
    showDeleteConfirmationModal({
      onConfirm: () => {
        deleteAlbumCollection({ id })
      },
    })
  }

  return [
    {
      title: 'Наименование',
      ellipsis: true,
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder:
        sort.field === 'name'
          ? sort.order === 'asc'
            ? 'ascend'
            : sort.order === 'desc'
              ? 'descend'
              : null
          : null,
      render: (name, _) => name,
    },
    {
      title: 'Автор',
      width: COLUMN_WIDTH.AUTHOR,
      dataIndex: 'author',
      key: 'author',
      sorter: true,
      sortOrder:
        sort.field === 'author'
          ? sort.order === 'asc'
            ? 'ascend'
            : sort.order === 'desc'
              ? 'descend'
              : null
          : null,
      render: (_, albumCollection) => {
        if (!albumCollection.authors) return ''
        const mainAuthors = albumCollection.authors.filter((a) => !a.isCoauthor)
        const coAuthors = albumCollection.authors.filter((a) => a.isCoauthor)
        const authors = [...mainAuthors, ...coAuthors]
        return (
          <>
            {authors.map((author, i) => {
              const isLast = i === authors.length - 1
              const route = ROUTES.AUTHORS.EDIT.replace(':id', author.id)

              return (
                <span key={author.id}>
                  <Typography.Link
                    onClick={() => void navigateToFx({ to: route })}
                  >
                    {author.nickname}
                  </Typography.Link>
                  {!isLast && ',\u00A0'}
                </span>
              )
            })}
          </>
        )
      },
    },
    {
      title: '',
      width: COMMON_COLUMN_WIDTH.ACTIONS,
      key: 'actions',
      align: 'center',
      render: (_, albumCollection) => (
        <Space size="middle">
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(albumCollection.id)}
          />
          <Button
            onClick={() => handleDelete(albumCollection.id)}
            icon={<DeleteOutlined style={{ color: 'red' }} />}
          />
        </Space>
      ),
    },
  ]
}
